import { Card } from "@procurement-agents/ui";
import type { CostSummary, RunState, SpeechSummary } from "@procurement-agents/scope-api";
import { relativeTime } from "../labels";

const NAMES: Record<string, string> = { next_question: "Phrase question", ask_question: "Ask", record_answer: "Record answer", extract_prefill: "Extract from description", confirm_prefill: "Confirm prefill", intake_gate: "Intake gate", requisition: "Requisition", review_requisition: "Requisition review", map_requisition_edit: "Read the correction", market_research: "Market research", select_structure: "Apply thresholds", structure_review: "Structure review", ask_procurement: "Structure decision", apply_structure_decision: "Record decision", select_template: "Select template", plan_sections: "Plan sections", review_plan: "Plan review", draft_sow: "Draft", qa_review: "QA review", qa_gate: "QA gate", apply_fixes: "Apply fixes", build_sme_queue: "SME queue", ask_sme: "SME clarification", regenerate_section: "Regenerate section", finish: "Run complete", review_draft: "Draft review", review_qa: "QA review stop", edit_chat: "Editing agent", edit_verify: "Proposal verification", qa_recheck: "Section re-check", edit_section: "Section edit", dismiss_finding: "Finding dismissed", proposal_rejected: "Proposal rejected", speech_synthesize: "Spoken aloud", speech_transcribe: "Dictation" };

/** The stops: an entry here is written when someone answers, so the gap before it is their time, not the system's. */
const STOPS = new Set(["ask_question", "confirm_prefill", "intake_review", "review_requisition", "handoff_choice", "ask_procurement", "review_plan", "review_draft", "review_qa", "ask_sme", "final_review"]);

/** "12 s", "4 min", "18 h", "3 days". */
export function span(seconds: number): string {
  if (seconds < 60) return `${seconds} s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
  if (seconds < 172800) return `${Math.round(seconds / 3600)} h`;
  return `${Math.round(seconds / 86400)} days`;
}

/** KRE-125: an audit summary in the reader's words. Summaries are written for the record ("skipped=[]",
 *  "needs_sme=['working_hours']", "{'critical': 0}"); here lists read as titles, dictionaries as "critical 0",
 *  key=value as "key: value", and a section's id as its title. */
export function plainSummary(text: string, titles: Record<string, string> = {}): string {
  const name = (raw: string) => titles[raw] ?? raw;
  let out = text.replace(/\[([^[\]]*)\]/g, (_, inner: string) => {
    const items = [...inner.matchAll(/'([^']*)'|"([^"]*)"/g)].map((m) => name(m[1] ?? m[2] ?? ""));
    return items.length ? items.join(", ") : inner.trim() ? inner : "none";
  });
  out = out.replace(/\{([^{}]*)\}/g, (_, inner: string) => [...inner.matchAll(/'([^']*)':\s*([^,]+)/g)].map((m) => `${m[1]} ${m[2]!.trim()}`).join(", ") || inner);
  out = out.replace(/\b([a-z][a-z_]*)=/g, (_, key: string) => `${key.replace(/_/g, " ")}: `);
  out = out.replace(/\bTrue\b/g, "yes").replace(/\bFalse\b/g, "no");
  for (const [id, title] of Object.entries(titles)) out = out.replace(new RegExp(`(?<![\\w-])${id}(?![\\w-])`, "g"), title);
  return out;
}

export function ActivityReader({ state, cost, agentConfigVersion }: { state: RunState; cost: CostSummary | null; agentConfigVersion?: number | null }) {
  const log = state.audit_log;
  const titles = Object.fromEntries(Object.values(state.sow ?? {}).map((d) => [d.section_id, d.title]));
  return (
    <Card title="Activity & cost" aside={agentConfigVersion != null ? <span className="muted small" title="The published agent configuration this run was frozen with (SPEC_SLICE6)">Agent configuration v{agentConfigVersion}</span> : undefined}>
      <ol className="timeline">
        {log.map((e, i) => {
          const prev = log[i - 1];
          const dur = prev ? Math.round((new Date(e.ts).getTime() - new Date(prev.ts).getTime()) / 1000) : null;
          // KRE-125: a stop's gap is the people's time ("waited 18 h"); work says how long it took; the model is a tooltip
          const time = dur == null || dur <= 0 ? "" : STOPS.has(e.node) ? ` · waited ${span(dur)}` : e.model_calls ? ` · took ${span(dur)}` : "";
          const what = e.node.startsWith("speech_") ? "speech" : e.model_calls ? `${e.model_calls} model call${e.model_calls === 1 ? "" : "s"}` : STOPS.has(e.node) ? "answered" : "no model";
          return (
            <li key={i}>
              <span className="muted small" title={e.ts}>{relativeTime(e.ts)}</span>
              <strong>{NAMES[e.node] ?? e.node.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase())}</strong>
              <span className="muted small" title={e.model ?? undefined}>{what}{time}</span>
              <p className="small">{plainSummary(e.summary, titles)}</p>
            </li>
          );
        })}
      </ol>
      {cost && <p className="cost">{costLine(cost)}</p>}
      {cost?.speech && (cost.speech.synthesize_calls > 0 || cost.speech.transcribe_calls > 0) && <p className="cost">{speechLine(cost.speech)}</p>}
    </Card>
  );
}

/** Speech in the units the providers bill by, apart from model calls (CHANGE_voice_level2). */
export function speechLine(s: SpeechSummary): string {
  const parts: string[] = [];
  if (s.synthesize_calls) parts.push(`${s.characters.toLocaleString()} characters spoken in ${s.synthesize_calls} request${s.synthesize_calls === 1 ? "" : "s"}`);
  if (s.transcribe_calls) parts.push(`${Math.round(s.audio_ms / 1000)} s of dictation in ${s.transcribe_calls} recording${s.transcribe_calls === 1 ? "" : "s"}`);
  return `Speech: ${parts.join(" · ")}`;
}

/** One line: calls, and the tokens the provider reported when the run has them. */
export function costLine(cost: CostSummary): string {
  const n = (x: number) => x.toLocaleString("en-AU");
  const tokens = cost.input_tokens_total != null ? ` · ${n(cost.input_tokens_total)} input and ${n(cost.output_tokens_total ?? 0)} output tokens` : "";
  return `${cost.model_calls_total} model calls in total${tokens}`;
}
